import { toUtcSeconds } from '@/utils/utcTime'
import { computePriceFormat } from '@/utils/pricePrecision'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { BubbleFillSeries } from './BubbleFillSeries.js'
import { LabelOverlaySeries } from './LabelOverlaySeries.js'
import { renderLevels } from './levels.js'

/**
 * Orderflow Quantum -- indicador de plataforma (modelo PRQ,
 * docs/specs/Spec_Orderflow_Indicador_I1_I2.md). Frontend PURO renderizador:
 * cero lógica de detección aquí -- todo (señales A-G, ciclo de vida
 * emitted/annulled, burbujas de scanMaxDelta) sale de
 * GET /api/public/indicators/orderflow-quantum (invariante permanente de la
 * plataforma). Este archivo solo dibuja `result.signals`/`result.bubbles`
 * tal cual llegan, igual que ict_quantum con su patrón externalData.
 */

// ─── Datos externos (inyectados desde la página del chart) ─────────────────

let externalData = null
let externalVersion = 0

export function setExternalData(data) {
  externalData = data
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Stats externos (WP-OQ-panel-v2) ───────────────────────────────────────
// `current_levels` (caja SL/TP de la última señal) viaja en el payload de
// /stats, que la página pide en un request independiente del de señales.
// Se guarda junto con la clave símbolo|TF con la que se pidió: detect() solo
// pinta niveles cuya clave coincide con el chart actual (tras un cambio de
// TF los stats viejos no deben pintar sobre el chart nuevo).
let externalStats = null

export function setExternalStats(stats, contextKey = null) {
  externalStats = stats ? { data: stats, key: contextKey } : null
  externalVersion++
}

export function getExternalStats() {
  return externalStats
}

// ─── Configuración por defecto (I1.4 del spec: únicos ajustes visibles) ────

export const defaultConfig = {
  _version: 1,
  showBubbles: true, // toggle "OrderFlow" del Pine (iBig)
  showLong: true,
  showShort: true,
  showLevels: true, // WP-OQ-panel-v2: caja SL/TP de la última señal en el chart
  orderflowLtf: '1m', // resolución de las burbujas (iLTF)
  swingLtf: '5m', // resolución de agresión en mecha de la señal (iLTFsw)
  // ── I3: modelo de salida / budget (simulación) ──────────────────────────
  // null = usa el default resuelto por el backend (paso 0, condicional a
  // la resolución para tp_r) -- se rellena con el valor EFECTIVO devuelto
  // por /stats tras el primer fetch (Spec_Orderflow_Indicador_I3_Stats.md
  // Enmienda 3: "el usuario nunca ve un default implícito"). Cambio
  // aditivo: no requiere bump de _version (mergeWithDefaults ya rellena
  // claves nuevas ausentes en configs guardadas con estos defaults).
  slBufferAtr: null,
  tp1R: null,
  tp2R: null,
  tp3R: null,
  budgetUsdt: 1000,
  riskPct: 10,
  leverage: null,
}

// ─── detect() -- passthrough puro, sin cómputo propio ──────────────────────

export function detect(candles, config, symbol, timeframe) {
  if (!externalData) return null
  const statsMatch = externalStats && externalStats.key === `${symbol}|${timeframe}`
  return {
    ...externalData,
    // Bloque del backend tal cual (o null: sin señal / stats aún no llegados /
    // stats de otro símbolo o TF). Nada que calcular aquí.
    currentLevels: statsMatch ? (externalStats.data.current_levels ?? null) : null,
    // Misma precisión que el eje nativo (computePriceFormat, como rango_quantum).
    pricePrecision: computePriceFormat(candles).precision,
    _dataVersion: externalVersion,
  }
}

// ─── render() -- solo dibuja lo que ya calculó el backend ──────────────────

const _SIDE_COLOR = {
  long: 'rgb(31,123,13)', // cBuy del Pine
  short: 'rgb(186,34,46)', // cSell del Pine
}

export function render(chart, candleSeries, result, config, api) {
  if (!result) return
  const cfg = { ...defaultConfig, ...(config || {}) }

  // ── Burbujas de OrderFlow ─────────────────────────────────────────────
  if (cfg.showBubbles && Array.isArray(result.bubbles) && result.bubbles.length > 0) {
    const bubbleSeries = api.addCustomSeries(new BubbleFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(148,163,184,0.5)',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
    })
    if (bubbleSeries) {
      const seriesData = result.bubbles
        .map(b => ({
          time: toUtcSeconds(b.time),
          price: b.price,
          sizeTier: b.size_tier,
          // `bubbleColor`, no `color` -- ver comentario de procedencia en
          // BubbleFillSeries.js (campo reservado que lightweight-charts
          // descarta de originalData).
          bubbleColor: b.color,
        }))
        .filter(d => d.time != null && d.price != null)
        .sort((a, b) => a.time - b.time)
      bubbleSeries.setData(seriesData)
    }
  }

  // ── Señales (Long/Short) -- solo clash_state === "emitted" ─────────────
  // El backend calcula el ciclo de vida completo (emitted/annulled) como
  // función pura de la ventana en cada respuesta -- el frontend jamás hace
  // diffing entre respuestas, solo filtra por este campo (Enmienda de
  // revisión de Marc, 2026-09-16, punto 2 del WP I1+I2).
  const labelsByTime = {}
  for (const sig of result.signals || []) {
    if (sig.clash_state !== 'emitted') continue
    const isLong = sig.side === 'long'
    if (isLong && !cfg.showLong) continue
    if (!isLong && !cfg.showShort) continue

    const t = toUtcSeconds(sig.signal_time)
    if (t == null) continue

    // Ancla del label (I1.3 del spec): LONG -> emitLow - 1xATR, SHORT ->
    // emitHigh + 1xATR. Aritmética de posicionamiento pura sobre valores ya
    // calculados por el backend (low/high/atr de la vela de señal), no es
    // detección de señal.
    const price = isLong ? sig.low - sig.atr : sig.high + sig.atr

    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push({
      text: isLong ? 'Long ▲' : 'Short ▼',
      price,
      anchor: isLong ? 'below' : 'above',
      // Placa con fondo del color de lado + texto blanco (estilo PRQ) --
      // 'plain' (solo texto, sin fondo) era ilegible en temas claros: texto
      // blanco sobre fondo crema/blanco (hallazgo de Marc, revisión visual).
      color: _SIDE_COLOR[sig.side] || '#94a3b8',
      textColor: '#FFFFFF',
      plaqueStyle: 'plaque',
    })
  }

  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle: 'plaque',
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map(t => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
    }
  }

  // ── Caja SL/TP de la última señal (WP-OQ-panel-v2) ─────────────────────
  // Solo la última (la del panel Estado); toggle default ON. El bloque
  // llega resuelto del backend -- levels.js solo lo dibuja.
  if (cfg.showLevels !== false && result.currentLevels) {
    renderLevels(chart, candleSeries, result.currentLevels, api, result.pricePrecision)
  }
}

export default {
  id: 'orderflow_quantum',
  name: 'Orderflow Quantum',
  version: '1.0.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
